import { useMemo } from "react";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { MousePointerClick, Phone, Mail, MessageCircle, FileText, ExternalLink, Link2, CalendarCheck, ClipboardList, Ban } from "lucide-react";

/**
 * Universaler CTA-Aktions-Editor.
 *
 * Props:
 *   value: { type: string, value: string }
 *   onChange: (next: { type, value }) => void
 *   customPages: [{slug, title}]  — für subpage-Dropdown
 *   sections: [{id, label}]       — für anchor-Dropdown (optional)
 *   companyPhone / companyEmail: Fallback-Hinweise
 *   compact: boolean              — komprimierte Darstellung (1 Zeile)
 *   testidPrefix: string          — für data-testid
 */
export function CtaActionEditor({
  value,
  onChange,
  customPages = [],
  sections = DEFAULT_SECTIONS,
  companyPhone = "",
  companyEmail = "",
  compact = false,
  testidPrefix = "cta-action",
}) {
  const current = value && typeof value === "object" ? value : { type: "default", value: "" };
  const type = current.type || "default";
  const val = current.value || "";

  const update = (patch) => {
    onChange({ type: current.type || "default", value: current.value || "", ...patch });
  };

  const cfg = useMemo(() => ACTION_CONFIG.find(a => a.key === type) || ACTION_CONFIG[0], [type]);

  return (
    <div className={`space-y-1.5 ${compact ? "" : "pt-1"}`} data-testid={`${testidPrefix}-editor`}>
      <Label className="text-[10px] uppercase tracking-wide text-muted-foreground flex items-center gap-1">
        <MousePointerClick size={10} /> Button-Aktion
      </Label>
      <Select value={type} onValueChange={(v) => update({ type: v, value: "" })}>
        <SelectTrigger className="rounded-md text-xs h-8" data-testid={`${testidPrefix}-type`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {ACTION_CONFIG.map(a => {
            const Icon = a.icon;
            return (
              <SelectItem key={a.key} value={a.key} data-testid={`${testidPrefix}-opt-${a.key}`}>
                <span className="inline-flex items-center gap-2">
                  <Icon size={11} className="text-muted-foreground" />
                  <span>{a.label}</span>
                </span>
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>

      {/* Wert-Input je nach Action-Type */}
      {type === "subpage" && (
        <Select value={val} onValueChange={(v) => update({ value: v })}>
          <SelectTrigger className="rounded-md text-xs h-8" data-testid={`${testidPrefix}-subpage`}>
            <SelectValue placeholder="Unterseite wählen…" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="index">Startseite</SelectItem>
            {customPages.filter(cp => cp?.slug).map((cp, i) => (
              <SelectItem key={cp.slug || i} value={cp.slug}>
                {cp.title || cp.slug}
              </SelectItem>
            ))}
            <SelectItem value="impressum">Impressum</SelectItem>
            <SelectItem value="datenschutz">Datenschutz</SelectItem>
          </SelectContent>
        </Select>
      )}

      {type === "anchor" && (
        <Select value={val} onValueChange={(v) => update({ value: v })}>
          <SelectTrigger className="rounded-md text-xs h-8" data-testid={`${testidPrefix}-anchor`}>
            <SelectValue placeholder="Bereich wählen…" />
          </SelectTrigger>
          <SelectContent>
            {sections.map(s => (
              <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {type === "phone" && (
        <Input
          value={val}
          onChange={e => update({ value: e.target.value })}
          placeholder={companyPhone ? `Standard: ${companyPhone}` : "+49 30 12345678"}
          className="rounded-md text-xs h-8 font-mono"
          data-testid={`${testidPrefix}-phone`}
        />
      )}

      {type === "email" && (
        <Input
          value={val}
          onChange={e => update({ value: e.target.value })}
          placeholder={companyEmail ? `Standard: ${companyEmail}` : "info@firma.de"}
          className="rounded-md text-xs h-8 font-mono"
          data-testid={`${testidPrefix}-email`}
        />
      )}

      {type === "whatsapp" && (
        <Input
          value={val}
          onChange={e => update({ value: e.target.value })}
          placeholder={companyPhone ? `Standard: ${companyPhone}` : "+49 176 12345678"}
          className="rounded-md text-xs h-8 font-mono"
          data-testid={`${testidPrefix}-whatsapp`}
        />
      )}

      {type === "url" && (
        <Input
          value={val}
          onChange={e => update({ value: e.target.value })}
          placeholder="https://…"
          className="rounded-md text-xs h-8 font-mono"
          data-testid={`${testidPrefix}-url`}
        />
      )}

      {!compact && cfg.hint && (
        <p className="text-[10px] text-muted-foreground leading-tight italic">{cfg.hint}</p>
      )}
    </div>
  );
}

const ACTION_CONFIG = [
  { key: "default",  label: "Smart-Default",           icon: MousePointerClick, hint: 'Öffnet das Buchungs-Modal wenn aktiv — sonst springt zum Kontaktformular.' },
  { key: "booking",  label: "Buchungs-Modal öffnen",    icon: CalendarCheck,     hint: 'Öffnet den konfigurierten Buchungs-Widget. Funktioniert nur wenn Buchung im Reiter „Buchung" aktiviert ist.' },
  { key: "inquiry",  label: "Projekt-Anfrage öffnen",   icon: ClipboardList,     hint: 'Öffnet das mehrstufige Projekt-Anfrage-Formular (Malern, Handwerker, etc.).' },
  { key: "contact",  label: "Zu Kontaktformular scrollen", icon: FileText,       hint: 'Scrollt sanft zum Kontaktformular auf der Startseite (#contact).' },
  { key: "phone",    label: "Anruf starten (tel:)",     icon: Phone,             hint: 'Öffnet die Telefon-App mit der eingegebenen Nummer (oder Firmennummer).' },
  { key: "whatsapp", label: "WhatsApp-Chat öffnen",      icon: MessageCircle,     hint: 'Öffnet wa.me mit der Nummer — direkt in WhatsApp Web/App.' },
  { key: "email",    label: "E-Mail schreiben (mailto:)", icon: Mail,            hint: 'Öffnet die E-Mail-App mit der angegebenen Adresse.' },
  { key: "subpage",  label: "Zu einer Unterseite linken", icon: Link2,           hint: 'Linkt auf eine andere Seite der Website.' },
  { key: "anchor",   label: "Zu einem Bereich scrollen", icon: Link2,            hint: 'Scrollt innerhalb der aktuellen Seite zu einem Sektions-Anker.' },
  { key: "url",      label: "Externer Link",             icon: ExternalLink,      hint: 'Öffnet eine externe URL in einem neuen Tab.' },
  { key: "none",     label: "Kein Link (deaktiviert)",   icon: Ban,                hint: 'Der Button wird angezeigt, ist aber nicht klickbar — nützlich für reine Optik.' },
];

const DEFAULT_SECTIONS = [
  { id: "hero",         label: "Hauptbereich (Hero)" },
  { id: "about",        label: "Über uns" },
  { id: "services",     label: "Leistungen" },
  { id: "stats",        label: "Zahlen & Fakten" },
  { id: "team",         label: "Team" },
  { id: "testimonials", label: "Kundenstimmen" },
  { id: "pricing",      label: "Preise" },
  { id: "gallery",      label: "Galerie" },
  { id: "contact",      label: "Kontaktformular" },
];

export const CTA_ACTION_PRESETS = ACTION_CONFIG;
